import { useLingui } from '@lingui/react/macro';
import type { FC, ReactNode } from 'react';
import type { WealthCoin } from '@/backend';
import { useCoins } from '@/shared/api';
import { Coin, type CoinProps } from '@/shared/Coin';
import { Select } from '@/shared/Select';

type CoinSelectProps = {
  value?: string;
  onChange?: (newValue?: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  size?: 8 | 10 | 12;
  customLabel?: (coin: WealthCoin) => ReactNode;
  labelClassName?: string;
  labelCoinProps?: Partial<CoinProps>;
  filteredKeys?: string[];
};

export const CoinSelect: FC<CoinSelectProps> = ({
  value,
  onChange,
  placeholder,
  required,
  className,
  size = 12,
  customLabel,
  labelClassName,
  labelCoinProps,
  filteredKeys,
}) => {
  const { t } = useLingui();
  const coins = useCoins();
  const filtered =
    coins.data?.filter(
      (c) => !(filteredKeys ?? []).includes(c.slug.toUpperCase()),
    ) ?? [];
  const options =
    filtered.map((c) => ({
      key: c.slug,
      value: c.slug.toUpperCase(),
      label: () =>
        customLabel ? (
          customLabel(c)
        ) : (
          <div
            className={`flex justify-between items-center gap-2 min-w-52 w-full ${labelClassName ?? ''}`}
          >
            <Coin coin={c} size={6} {...labelCoinProps} />
          </div>
        ),
    })) ?? [];

  return (
    <Select
      size={size}
      value={value?.toUpperCase()}
      onChange={onChange}
      placeholder={placeholder ?? t`Select Coin`}
      className={className}
      required={required}
      options={options}
      loading={coins.isLoading}
      disabled={coins.isLoading || options.length === 0}
    />
  );
};
